import { format, isAfter, isDate, isEqual, isPast, isToday, isValid, isWeekend, startOfDay } from 'date-fns';
import messages from './_messages';

/**
 * Function to check if the given date is allowed based on the prop values
 */
export function checkIsOutsideRange(date, _ref) {
  let {
    allowPastDates,
    allowWeekends,
    maxDate,
    minDate
  } = _ref;
  const today = isToday(date);
  if (!allowPastDates && !minDate && isPast(date) && !today) {
    return true;
  }
  if (!allowWeekends && isWeekend(date)) {
    return true;
  }
  if (minDate && isAfter(startOfDay(minDate), date)) {
    return true;
  }
  if (maxDate && isAfter(date, maxDate)) {
    return true;
  }
  return false;
}

/**
 * Function to check if the given date is a date and a valid one
 */
export function validateDate(date) {
  return isValid(date) && isDate(date);
}

/**
 * Function to check if the given date is allowed compared to the array of disabled days
 */
export function checkIsDayBlocked(date, disabledDays) {
  return disabledDays == null ? void 0 : disabledDays.some(disabledDay => {
    return isEqual(date, disabledDay);
  });
}

/**
 * Function to check that the given date has four charaters in the year.
 * The function should be called only when the default date format is used.
 */
export function checkYearLength(date) {
  var _date$split$;
  if (!date || date.split('.').length <= 2) {
    return true;
  }
  return ((_date$split$ = date.split('.')[2]) == null ? void 0 : _date$split$.length) === 4;
}
/**
 * Function to return a specific error message and type based on allowed and not allowed dates.
 *
 * The order of the code matters since the error messages are prioritized. For example if past dates and weekends are not allowed and
 * a past weekend date is selected, show "past dates are not allowed" instead of specifying that weekends are not allowed.
 */
export function getErrorMessageAndType(date, _ref2, inputValue) {
  let {
    dateFormat,
    dateRangeChecks: {
      allowPastDates,
      allowWeekends,
      maxDate,
      minDate
    },
    disabledDays,
    isDayBlocked,
    isOutsideRange,
    lc: locale
  } = _ref2;
  const errMsgs = messages.errorMessages;
  const errorTypes = getErrorTypes();

  // Error message for an invalid date object
  if (!validateDate(date)) {
    return {
      errorMessage: errMsgs.notDate[locale],
      errorType: errorTypes.notDate
    };
  }

  // Error message for the year not being four characters long
  if (!checkYearLength(inputValue)) {
    return {
      errorMessage: errMsgs.yearNotFullLength[locale],
      errorType: errorTypes.yearNotFullLength
    };
  }

  // Error message for a past date when past dates are not allowed
  if (!allowPastDates && !minDate && !maxDate && isPast(date) && !isToday(date)) {
    return {
      errorMessage: errMsgs.pastDatesNotAllowed[locale],
      errorType: errorTypes.pastDatesNotAllowed
    };
  }

  // Error message for a date that is earlier than the minimum date
  if (minDate && isAfter(startOfDay(minDate), date)) {
    return {
      errorMessage: errMsgs.blockedByMinDate[locale]({
        dateForErrMsg: format(minDate, dateFormat)
      }),
      errorType: errorTypes.blockedByMinDate
    };
  }

  // Error message for a date that is later than the maximum date
  if (maxDate && isAfter(date, maxDate)) {
    return {
      errorMessage: errMsgs.blockedByMaxDate[locale]({
        dateForErrMsg: format(maxDate, dateFormat)
      }),
      errorType: errorTypes.blockedByMaxDate
    };
  }

  // Error message for a weekend date when weekends are not allowed
  if (!allowWeekends && isWeekend(date)) {
    return {
      errorMessage: errMsgs.weekendsNotAllowed[locale],
      errorType: errorTypes.weekendsNotAllowed
    };
  }

  // Error message when a date is blocked by disabledDays, isDayBlocked or isOutsideRange props
  if (checkIsDayBlocked(date, disabledDays) || isDayBlocked != null && isDayBlocked(date) || isOutsideRange != null && isOutsideRange(date)) {
    return {
      errorMessage: errMsgs.blocked[locale],
      errorType: errorTypes.blocked
    };
  }
  return null;
}

/**
 * Function to return error types which are basically the keys of error message types.
 */
export function getErrorTypes() {
  const errorTypes = {};
  for (const err of Object.keys(messages.errorMessages)) {
    errorTypes[err] = err;
  }
  return errorTypes;
}